import * as borogove from "borogove";
import { useState } from "react";

export const useAttachmentFetch = (
	client: borogove.Client,
	chat: borogove.Chat,
	message: borogove.ChatMessage,
) => {
	const [fetchState, setFetchState] = useState<FetchState>(() =>
		buildAttachmentState(
			message.attachments.map((a) => [a, getAlreadyFetchedAttachment(chat, a)]),
			(result): result is null => result === null,
			(fetched, unfetched): UnfetchedState => ({
				status: Status.Unfetched,
				fetched,
				unfetched,
			}),
		),
	);

	const fetchAttachments = async () => {
		if (
			fetchState.status === Status.Fetched ||
			fetchState.status === Status.Fetching
		)
			return;

		setFetchState({ status: Status.Fetching, fetched: fetchState.fetched });

		const attachmentResults = await Promise.all(
			message.attachments.map(async (a) => {
				const result =
					fetchState.fetched.get(a) ??
					(await fetchAttachmentAndLogErrors(message, a, client));

				return [a, result] satisfies [
					borogove.ChatAttachment,
					FetchedAttachment | Error,
				];
			}),
		);

		setFetchState(
			buildAttachmentState(
				attachmentResults,
				(result): result is Error => result instanceof Error,
				(fetched, failed): FailedState => ({
					status: Status.Failed,
					fetched,
					failed,
				}),
			),
		);
	};

	return [fetchState, fetchAttachments] as const;
};

const getAlreadyFetchedAttachment = (
	chat: borogove.Chat,
	attachment: borogove.ChatAttachment,
): FetchedAttachment | null => {
	if (attachment.cachedAt)
		return {
			uri: attachment.cachedAt,
			embeddable: true,
		};

	const http = attachment.uris.find((uri) => uri.startsWith("http"));
	const isPublicChannel = chat instanceof borogove.Channel && !chat.isPrivate();
	if (http && isPublicChannel) {
		const domain = chat.chatId.split(/[@/]/)[1];
		const onChatDomain = new URL(http).hostname.endsWith(domain);

		return {
			uri: http,
			embeddable: chat.isTrusted() && onChatDomain,
		};
	}

	return null;
};

const fetchAttachmentAndLogErrors = (
	...args: Parameters<typeof fetchAttachment>
) =>
	fetchAttachment(...args).then((result) => {
		if (result instanceof Error) console.error(result);
		return result;
	});

const fetchAttachment = async (
	message: borogove.ChatMessage,
	attachment: borogove.ChatAttachment,
	client: borogove.Client,
): Promise<FetchedAttachment | Error> => {
	try {
		const result = await message.fetchAttachment(attachment, client);

		if (result.cachedAt) {
			return {
				uri: result.cachedAt,
				embeddable: true,
			};
		} else {
			return new Error(
				`Unable to fetch attachment, cachedAt is still null: ${JSON.stringify(result)}`,
			);
		}
	} catch (e) {
		return new Error("Attachment fetch failed", { cause: e });
	}
};

const buildAttachmentState = <T, S>(
	attachmentResults: [borogove.ChatAttachment, FetchedAttachment | T][],
	isRemaining: (result: FetchedAttachment | T) => result is T,
	incompleteState: (
		fetched: Fetched,
		remaining: Map<borogove.ChatAttachment, T>,
	) => S,
): FetchedState | S => {
	const fetched: Fetched = new Map();
	const remaining = new Map<borogove.ChatAttachment, T>();

	for (const [attachment, result] of attachmentResults) {
		if (isRemaining(result)) {
			remaining.set(attachment, result);
		} else {
			fetched.set(attachment, result);
		}
	}

	if (remaining.size > 0) {
		return incompleteState(fetched, remaining);
	}

	return { status: Status.Fetched, fetched };
};

type Fetched = Map<borogove.ChatAttachment, FetchedAttachment>;
type Unfetched = Map<borogove.ChatAttachment, null>;
type Failed = Map<borogove.ChatAttachment, Error>;

export type FetchedAttachment = {
	uri: string;
	embeddable: boolean;
};

export enum Status {
	Unfetched,
	Fetching,
	Failed,
	Fetched,
}

type UnfetchedState = {
	status: Status.Unfetched;
	fetched: Fetched;
	unfetched: Unfetched;
};

type FetchingState = {
	status: Status.Fetching;
	fetched: Fetched;
};

type FailedState = {
	status: Status.Failed;
	fetched: Fetched;
	failed: Failed;
};

type FetchedState = {
	status: Status.Fetched;
	fetched: Fetched;
};

type FetchState = UnfetchedState | FetchingState | FailedState | FetchedState;
